// @flow
import get from 'lodash/get';

import { createAction } from 'redux-act';

import type { StaticHandlerArgs } from '@gandi/app-runner/server';
import {
  saveTaxesDisplaySetting,
  selectCurrentCurrency,
  selectUserCountryCode,
} from '@gandi/authentication';
import { selectPricingInfos, preFetchPricingInfos } from '@gandi/billing';
import {
  getCart,
  createCart,
  addDiscount,
  selectCartUUID,
  selectOneDiscount,
  selectCartIsReopenable,
  selectCartIsActive,
  selectCartHasOrga,
  selectCartProductOwner,
  refreshCart,
  resumeCart,
  fetchCartConfigurations,
  selectCartConfigurations,
  applyTemplate as applyTemplate_,
  selectProductsByType,
  retrieveDefaultTemplate,
  addProducts,
  retrieveCart,
  selectCartAffiliate,
  selectCartCurrency,
  cartCheckoutResource,
} from '@gandi/cart';
import {
  selectOneOrganization,
  fetchOneOrganization,
} from '@gandi/redux-organization';
import { contactbook } from '@gandi/redux-resource-contactbook';
import { fetchCurrentSharing, selectCurrentId } from '@gandi/redux-sharing';
import type { FetchResponse } from '@gandi/resource/redux';
import { resources } from '@gandi/template2';
import type { TranslatorProps } from '@gandi/translate';

import { GET } from 'gandi.redux-fetcher';
import { logging } from 'gandi.redux-logger';

import { applyEmptyTemplate } from 'src/Cart/Setup/Overview/components/Template/modules/actions';
import {
  selectDomainCreateAndTransfer,
  selectSetupOwner,
  selectShouldFetchFavoriteContacts,
  selectSetupOwnerChanged,
} from 'src/Cart/Setup/modules/selectors';

import { appendServicesBulkData } from '../../DomainRenew/modules/domainServices';
import type { BulkProducts } from '../../DomainRenew/types';

import type {
  Dispatch,
  FullState,
  GetState,
  ResourceThunkAction,
} from '../../types';

import { selectCorporatePackMonthlyPrice } from './selectors/corporatePack';

type AffiliateRequestQuery = {
  affiliate?: string,
  referer?: string,
  ah?: string,
  discount?: string,
  size?: string,
};

type HandleRedirectParams = {
  // sometimes there is no cartId in params
  cartId?: string,
  ...
};

type HandleRedirectQuery = {
  currency?: string,
  country?: string,
  ...AffiliateRequestQuery,
  ...
};

export type StaticHandlerArgsWithMarketing<T = {}> = StaticHandlerArgs<
  T,
  HandleRedirectParams,
  HandleRedirectQuery,
  FullState,
>;

type CreateActionReturn<T = $ReadOnlyArray<mixed>> = {
  (...T): { meta?: mixed, payload: mixed, type: string, ... },
  assignTo: mixed => void,
  assigned: () => boolean,
  bindTo: mixed => mixed,
  bound: () => boolean,
  dispatched: () => boolean,
  getType: () => string,
  raw: () => mixed,
  toString: () => string,
  ...
};

const loggerNamespace = 'Shop/modules/actions';

// Useful to up a flag when coming from server side
// (to avoid to do componentDidMount stuff without checking a lot of stuff)
// FIXME: Implement "complex" selectors and remove this error-prone hack.
export const setPrefetchStatus: CreateActionReturn<> = createAction(
  '[Shop] Set preFetch status',
);

/**
 * Useful for global spinner status.
 */
export const setAppWorking: CreateActionReturn<> = createAction(
  '[Shop] Set working flag',
);
export const unsetAppWorking: CreateActionReturn<> = createAction(
  '[Shop] Unset working flag',
);

export const patchDomainContactsStart: CreateActionReturn<> = createAction(
  '[Shop] Patch domain contacts [START]',
);
export const patchDomainContactsEnd: CreateActionReturn<> = createAction(
  '[Shop] Patch domain contacts [END]',
);
// XXX: this requires redux-act definition to be useful, but it requires a lot of work before making shop valid.
export type PatchDomainContactsEndAction = ReturnType<
  typeof patchDomainContactsEnd,
>;

export const setPayLaterProcess: CreateActionReturn<Array<any>> = createAction(
  '[Shop] Set payLaterProcess flag',
);

export const setCorporatePackPrice: CreateActionReturn<Array<any>> =
  createAction('Set Corporate Pack price');

export const setTrusteePrices: CreateActionReturn<
  Array<{ currency: any, prices: any }>,
> = createAction('Set Trustee Prices');

export const applyTemplateIfNeeded =
  (): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    // Force create template assoc for the current product/process
    // This might not be the best solution (the assoc doesn't exist until the user has displayed the Overview)
    // for now it just work as before plus the template removal support
    // FIXME: invalidate cart configuration when adding/removing a transfer/create product.
    //        Then, only fetch cart configurations if !isLoadedCartConfigurations
    await dispatch(fetchCartConfigurations());

    const productType = 'domain';
    const processes = ['transfer', 'create'];

    const configurations = selectCartConfigurations(getState());

    const setupOwnerChanged = selectSetupOwnerChanged(getState());

    const promises = processes
      .map(process => ({
        productType,
        process,
      }))
      .filter(
        kind =>
          selectProductsByType(getState(), {
            productType: kind.productType,
          })
            // list existing products according to the kind (productType and process)
            // XXX: could be more elegant (filter in filter)
            .filter(product => product.process === kind.process).length > 0,
      )
      .filter(
        kind =>
          // filter on missing configs
          // /!\ a config can exists without a templateId when it has been «removed»
          //      in that case, a default template must not be applied.
          //      cf. https://phabricator.corp.gandi.net/T64380
          configurations.find(
            config =>
              config.product_type === kind.productType &&
              config.process === kind.process,
          ) === undefined || setupOwnerChanged,
      )
      .map(async kind => {
        // XXX: use fetch all defaults instead https://doc.corp.gandi.net/api-v5-stage/template/#api-v5-templates-defaults
        const defaultTemplate = await dispatch(
          retrieveDefaultTemplate(`${kind.productType}_${kind.process}`),
        ).catch(() => {});

        if (!defaultTemplate) {
          const appliedConfig = configurations.find(
            config =>
              config.product_type === kind.productType &&
              config.process === kind.process,
          );

          if (!setupOwnerChanged || !appliedConfig) {
            return undefined;
          }

          return dispatch(applyEmptyTemplate(appliedConfig));
        }

        const { templateId, templateName, templateDescription } =
          defaultTemplate;

        return dispatch(
          applyTemplate_({
            ...kind,
            templateName,
            templateId,
            templateDescription,
            noConflict: setupOwnerChanged,
          }),
        );
      });

    await Promise.allSettled(promises).catch(() => {});
    return promises.length > 0
      ? dispatch(fetchCartConfigurations())
      : Promise.resolve();
  };

export const tryResumeCart =
  (): ((dispatch: Dispatch, getState: GetState) => Promise<void | mixed>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const logger = logging(dispatch, loggerNamespace);
    const cartIsReopenable = selectCartIsReopenable(getState());

    if (cartIsReopenable) {
      await dispatch(resumeCart()).catch(err => {
        logger.error({ err }, 'Error when resuming the cart.');

        throw err;
      });

      // Cart has been resumed, re-trigger initialization with a fresh state.
      return dispatch(getCart());
    }

    return Promise.resolve();
  };

/**
 * Track the global checkout sequence lifecycle.
 */
export const postCartCheckoutStart: CreateActionReturn<[]> = createAction(
  '[Shop] Post cart checkout [START]',
);
export const postCartCheckoutEnd: CreateActionReturn<[]> = createAction(
  '[Shop] Post cart checkout [END]',
);
export const postCartCheckout =
  (): ResourceThunkAction => (dispatch, getState) => {
    const cartId = selectCartUUID(getState());

    dispatch(postCartCheckoutStart());
    return dispatch(
      cartCheckoutResource.post(
        cartCheckoutResource.options.postPath,
        {},
        { params: { cartId } },
      ),
    )
      .then(() => dispatch(getCart()))
      .catch(() => {})
      .finally(() => {
        dispatch(postCartCheckoutEnd());
      });
  };

/**
 * Cart Overview Higher Order Action Creator.
 * Triggered on both componentDidMount and preFetch.
 */
export const initializeOverview =
  (args: {
    orderId?: string,
  }): ((
    Dispatch,
    GetState,
  ) => Promise<void | Array<ReturnType<<T>(p: Promise<T> | T) => T>>>) =>
  (dispatch: Dispatch, getState: GetState) => {
    const logger = logging(dispatch, loggerNamespace);

    const currentId = selectCurrentId(getState());
    const authenticated = !!currentId;
    const cartHadOrga = selectCartHasOrga(getState());

    return (
      Promise.resolve()
        // Retrieve the latest Cart state
        .then(() => dispatch(getCart()))
        .then(cart => {
          if (!cart) {
            const stop = new Error('No cart');
            stop.name = 'CartError';
            throw stop;
          }
          return Promise.resolve();
        })
        // Abort Overview initialization if cart owner must be specified by the user.
        .then(() => {
          const cartHasOrga = selectCartHasOrga(getState());
          if (cartHadOrga && !cartHasOrga) {
            const stop = new Error('Cart owner needs to be specified');
            stop.name = 'CartError';
            throw stop;
          }
        })
        // Refresh the cart to test if cart content is still ok
        .then(() => {
          if (args.orderId) {
            return dispatch(refreshCart())
              .then(() => dispatch(setPayLaterProcess()))
              .catch(err => {
                logger.error({ err }, 'Error when refresh the cart.');

                return dispatch(tryResumeCart());
              });
          }

          return Promise.resolve();
        })
        .then(() => {
          if (!args.orderId) {
            return dispatch(tryResumeCart());
          }

          return Promise.resolve();
        })
        // These calls can safely be performed in //
        .then(() => {
          if (!authenticated) {
            return Promise.resolve();
          }

          const productOwner = selectCartProductOwner(getState());
          const productOwnerOrg = selectOneOrganization(
            getState(),
            productOwner,
          );
          const shouldFetchOwnerOrg = productOwner && !productOwnerOrg;

          const setupOwner = selectSetupOwner(getState());
          const domainsCreateAndTransfer =
            selectDomainCreateAndTransfer(getState());
          const shouldFetchFavoriteContacts =
            domainsCreateAndTransfer.length > 0 &&
            selectShouldFetchFavoriteContacts(getState(), setupOwner);

          // Trigger dry-run calls but don't await them as they can be very long.
          // The page will re-render if it finds anything.
          if (global.__CLIENT__) dispatch(postCartCheckout());
          else dispatch(setAppWorking());

          return Promise.allSettled([
            dispatch(fetchCurrentSharing()),
            dispatch(fetchCartConfigurations()),
            // Swallow these exceptions as not blocker
            shouldFetchOwnerOrg &&
              dispatch(fetchOneOrganization(productOwner)).catch(() => {}),
            shouldFetchFavoriteContacts &&
              dispatch(contactbook.fetchContactsWithRole(setupOwner)).catch(
                () => {},
              ),
          ]);
        })
    );
  };

export const retrievePricing =
  (currency: string | void, taxes: string | void): (Dispatch => any) =>
  dispatch => {
    if (taxes) {
      dispatch(saveTaxesDisplaySetting(taxes === 'yes'));
    }
    return dispatch(preFetchPricingInfos(currency));
  };

const selectHasCart = (state: FullState) =>
  selectCartUUID(state) && selectCartIsActive(state);

/**
 * Apply affiliate and referer to the cart
 * @param  {string} affiliate
 * @param  {string} referer works only if affiliate is set. (Note that cart api does not send it back)
 * @param  {string} ah is the affiliate hash
 * @return {Promise}
 */
const applyAffiliate =
  (affiliate?: string, referer?: string, ah?: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    // Either ?affiliate [+ referer] or ?ah
    if (!affiliate && !ah) {
      return Promise.resolve();
    }

    const hasCart = selectHasCart(getState());
    const currentCartAffiliate = selectCartAffiliate(getState());
    const hasCorrectAffiliateAlready =
      hasCart && affiliate && currentCartAffiliate === affiliate;

    if (hasCorrectAffiliateAlready) {
      return Promise.resolve();
    }

    // Cannot retrieve from selectPricingInfos, user does not exist yet.
    // => use shop settings.
    const currency = selectCurrentCurrency(getState());
    const country = selectUserCountryCode(getState());

    return dispatch(
      // $FlowFixMe[incompatible-call]
      createCart({ currency, country, affiliate, referer, ah }),
    ).then(() => dispatch(getCart()));
  };

const applyDiscount =
  (discount?: string) => (dispatch: Dispatch, getState: GetState) => {
    if (!discount) {
      return Promise.resolve();
    }

    const hasCart = selectHasCart(getState());
    const hasDiscountAlready =
      hasCart && selectOneDiscount(getState(), { code: discount });

    if (hasDiscountAlready) {
      return Promise.resolve();
    }

    const doAddDiscount = () =>
      dispatch(addDiscount(String(discount))).then(() => dispatch(getCart()));

    if (hasCart) {
      return doAddDiscount();
    }

    const { currency, country } = selectPricingInfos(getState());
    return dispatch(createCart({ currency, country })).then(() =>
      doAddDiscount(),
    );
  };

// eslint-disable-next-line no-underscore-dangle
export const _applyTemplate =
  (
    templateId: void | string,
  ): ((dispatch: Dispatch, getState: GetState) => Promise<void | mixed>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    if (!templateId) {
      return Promise.resolve();
    }

    const hasCart = selectHasCart(getState());

    if (hasCart) {
      await dispatch(fetchCartConfigurations());
      const configurations = selectCartConfigurations(getState());

      const hasCorrectTemplateAlready =
        get(
          configurations.filter(
            config =>
              config.product_type === 'domain' && config.process === 'create',
          ),
          '[0].payload.template_id',
        ) == templateId; // eslint-disable-line eqeqeq

      if (hasCorrectTemplateAlready) {
        return Promise.resolve();
      }

      return dispatch(
        resources.templateResource.fetchOne(
          `/templates/templates/${templateId}`,
        ),
      ).then(response => {
        const applyTemplateArgs = {
          process: 'create',
          productType: 'domain',
          templateId: response.data.id,
          templateName: response.data.name,
          templateDescription: response.data.description,
          locked: true,
        };

        return dispatch(applyTemplate_(applyTemplateArgs));
      });
    }

    const { currency, country } = selectPricingInfos(getState());
    return dispatch(createCart({ currency, country }))
      .then(() => dispatch(getCart()))
      .then(() => dispatch(_applyTemplate(templateId)));
  };

const doRedirectAfterAffiliatesHandling =
  ({
    req,
    res,
    affiliate,
    referer,
    ah,
    discount,
  }: {
    ...StaticHandlerArgsWithMarketing<>,
    affiliate?: string,
    referer?: string,
    ah?: string,
    discount?: string,
  }) =>
  (dispatch: Dispatch, getState: GetState) => {
    if (!affiliate && !referer && !ah && !discount) {
      return Promise.resolve('redirection is not necessary');
    }

    const cartId = selectCartUUID(getState());

    const [currentPathname] = req.originalUrl.split('?');
    const cleanPathname = currentPathname.replace(/\/$/, '');
    const targetPath = `${cleanPathname}/${cartId}`;

    // $FlowFixMe[incompatible-call] Array values are correctly cast to comma-separated values
    const params = new URLSearchParams(req.query);

    ['affiliate', 'referer', 'ah', 'discount'].forEach(key =>
      params.delete(key),
    );

    const searchString = params.toString();
    const finalUrl = searchString
      ? `${targetPath}?${searchString}`
      : targetPath;

    res.redirect(finalUrl);

    return Promise.reject(new Error('redirection'));
  };

export const handleMarketingParams: (
  args: StaticHandlerArgsWithMarketing<>,
) => Promise<void> = args => {
  const { req, dispatch, params } = args;
  const { body, query } = req;

  const affiliate: string | void =
    get(query, 'affiliate') || get(body, 'affiliate');
  const referer: string | void = get(query, 'referer') || get(body, 'referer');
  const ah: string | void = get(query, 'ah') || get(body, 'ah');
  const discount: string | void =
    get(query, 'discount') || get(body, 'discount');
  const template: string | void =
    get(query, 'template') || get(body, 'template');

  if (!ah && !affiliate && !referer && !discount && !template) {
    return Promise.resolve();
  }

  return dispatch(retrieveCart(params.cartId))
    .then(() => dispatch(applyAffiliate(affiliate, referer, ah)))
    .then(() => dispatch(applyDiscount(discount)))
    .then(() => dispatch(_applyTemplate(template)))
    .then(() => {
      if (req.method !== 'POST') {
        return dispatch(
          doRedirectAfterAffiliatesHandling({
            affiliate,
            referer,
            ah,
            discount,
            ...args,
          }),
        );
      }

      return undefined;
    });
};

export const fetchCorporatePackMonthlyPrices =
  (): ((dispatch: Dispatch, getState: GetState) => Promise<void>) =>
  (dispatch: Dispatch, getState: GetState) => {
    if (selectCorporatePackMonthlyPrice(getState())) {
      return Promise.resolve();
    }

    const logger = logging(dispatch, loggerNamespace);

    const { country, sharingId, currency, grid } =
      selectPricingInfos(getState());

    const cartCurrency = selectCartCurrency(getState());

    const params = {
      processes: 'create',
      name: 'corpo_monthly',
      sharing_id: sharingId,
      currency: cartCurrency || currency,
      country,
      grid,
    };

    // $FlowFixMe[incompatible-type]
    if (!sharingId) delete params.sharing_id;

    return dispatch(
      GET('/prices/corporate', {
        params,
        // $FlowFixMe[incompatible-call]
        on200: (response: FetchResponse<200, any>) => {
          const packCurrency = response.data?.currency;
          const price = response.data?.products[0]?.prices[0];
          if (price !== undefined) {
            // TODO: here we are storing the price for one month
            // we may think about storing the full catalog
            // that will avoid to fetch it again for the cart overview.
            dispatch(
              setCorporatePackPrice({
                currency: packCurrency,
                priceAfterTaxes: price?.price_after_taxes,
                priceBeforeTaxes: price?.price_before_taxes,
              }),
            );
          }
        },
        not200: () => {
          logger.error('Unable to fetch Corporate Pack monthly prices.');
        },
      }),
    );
  };

// FIXME TYPE PROPERLY
export const fetchCorporateTrusteePrices =
  (): ((dispatch: Dispatch, getState: GetState) => any) =>
  (dispatch: Dispatch, getState: GetState) => {
    const logger = logging(dispatch, loggerNamespace);

    const { country, sharingId, currency, grid } =
      selectPricingInfos(getState());

    const params = {
      processes: 'create',
      name: 'trustee',
      sharing_id: sharingId,
      currency,
      country,
      grid,
    };

    return dispatch(
      GET('/prices/domain_option', {
        params,
        // $FlowFixMe[incompatible-call]
        on200: (response: FetchResponse<200, any>) => {
          const prices = response.data.products[0].prices;
          if (prices !== undefined) {
            dispatch(
              setTrusteePrices({ currency: response.data.currency, prices }),
            );
          }
        },
        not200: () => {
          logger.error('Unable to fetch Trustee prices.');
        },
      }),
    );
  };

export const addServicesOnConnect =
  (translator: TranslatorProps): (Dispatch => Promise<void | any>) =>
  async dispatch => {
    const bulkProducts: BulkProducts = await dispatch(
      appendServicesBulkData(translator, { data: [], extra: {} }, true),
    );

    if (!bulkProducts.data.length) {
      return Promise.resolve();
    }

    await dispatch(
      addProducts({
        productsData: bulkProducts.data,
        // $FlowFixMe sync types @gandi/cart AddProductsArgs with shop BulkProducts
        extra: bulkProducts.extra,
        options: {
          headers: { 'X-Dry-Run': 0 },
        },
      }),
    );

    return dispatch(getCart());
  };

export const setPreferencesDialogOpen: CreateActionReturn<Array<any>> =
  createAction('[layout: preferencesDialog] set open');
